import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { storefront } from '../lib/supabase';
import { effectivePrice, pickLang } from '../lib/format';
import ProductCard from '../components/ProductCard';
import ComboCard from '../components/ComboCard';

const FLAG_FILTERS = ['hot', 'best', 'limited', 'sale', 'favorite', 'combo'];

export default function Products() {
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');

  const filter = params.get('filter') || 'all';
  const setFilter = (f) => setParams(f === 'all' ? {} : { filter: f });

  useEffect(() => {
    Promise.all([storefront.products(), storefront.combos()])
      .then(([p, c]) => {
        if (p.error || c.error) throw p.error || c.error;
        setProducts(p.data || []);
        setCombos(c.data || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products]
  );

  const filtered = useMemo(() => {
    let list = products;
    if (filter === 'hot') list = list.filter((p) => p.is_hot_seasonal);
    else if (filter === 'best') list = list.filter((p) => p.is_best_seller);
    else if (filter === 'limited') list = list.filter((p) => p.is_limited);
    else if (filter === 'favorite') list = list.filter((p) => p.is_favorite);
    else if (filter === 'sale') list = list.filter((p) => Number(p.sale_price) && Number(p.sale_price) < Number(p.price));
    else if (filter !== 'all' && filter !== 'combo') list = list.filter((p) => p.category === filter);

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) => p.name?.toLowerCase().includes(q) || p.name_en?.toLowerCase().includes(q)
      );
    }
    if (sort === 'priceAsc') list = [...list].sort((a, b) => effectivePrice(a) - effectivePrice(b));
    else if (sort === 'priceDesc') list = [...list].sort((a, b) => effectivePrice(b) - effectivePrice(a));
    else list = [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, filter, search, sort]);

  const filteredCombos = useMemo(() => {
    if (filter !== 'combo') return [];
    let list = combos;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => c.name?.toLowerCase().includes(q) || c.name_en?.toLowerCase().includes(q));
    }
    return list;
  }, [combos, filter, search]);

  if (loading) return <div className="loading">{t('misc.loading')}</div>;
  if (error) return <div className="empty">{t('misc.error')}</div>;

  const chipLabel = (f) =>
    ({ all: t('p.all'), hot: `🔥 ${t('home.hot')}`, best: `⭐ ${t('home.best')}`, limited: `⏳ ${t('home.limited')}`,
       sale: `🏷️ ${t('home.sale')}`, favorite: `💛 ${t('b.fav')}`, combo: `🎁 ${t('p.combos')}` }[f] || f);

  return (
    <div className="container section">
      <h1>{t('nav.products')}</h1>

      <div className="row" style={{ marginBottom: 10 }}>
        {['all', ...FLAG_FILTERS].map((f) => (
          <button key={f} className={`chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {chipLabel(f)}
          </button>
        ))}
        {categories.map((c) => (
          <button key={c} className={`chip ${filter === c ? 'active' : ''}`} onClick={() => setFilter(c)}>
            {lang === 'en' ? products.find((p) => p.category === c)?.category_en || c : c}
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: 18 }}>
        <input
          style={{ maxWidth: 280 }}
          placeholder={t('p.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={{ maxWidth: 200 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="newest">{t('p.sort.newest')}</option>
          <option value="priceAsc">{t('p.sort.priceAsc')}</option>
          <option value="priceDesc">{t('p.sort.priceDesc')}</option>
        </select>
      </div>

      {filter === 'combo' ? (
        filteredCombos.length ? (
          <div className="grid grid-4">{filteredCombos.map((c) => <ComboCard key={c.id} combo={c} />)}</div>
        ) : (
          <div className="empty">{t('p.empty')}</div>
        )
      ) : filtered.length ? (
        <div className="grid grid-4">{filtered.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      ) : (
        <div className="empty">{t('p.empty')}</div>
      )}
    </div>
  );
}
